/** Approval inbox — requests pending the current user's action. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useTrainingInbox } from "../hooks";

function fmt(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtDateTime(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

export default function TrainingInboxPage() {
  const [page, setPage] = useState(0);
  const query = useTrainingInbox(page, 10);

  if (query.isLoading) return <PageLoader label="Loading approval inbox…" />;
  if (query.isError) return <ErrorState title="Failed to load inbox" message={String(query.error)} />;

  const data = query.data!;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Training Approvals</h1>
      </div>

      {data.content.length === 0 ? (
        <EmptyState title="No pending approvals" description="Training requests awaiting your action will appear here." />
      ) : (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-hidden divide-y">
            {data.content.map((r) => (
              <Link
                key={r.id}
                to={`/hr/training/${r.id}`}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{r.trainingTitle}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {r.employeeName ?? "Unknown"}
                    {r.departmentName ? ` · ${r.departmentName}` : ""}
                    {r.submittedAt ? ` · Submitted ${fmtDateTime(r.submittedAt)}` : ""}
                    {r.estimatedCost ? ` · ${r.currency ?? "KES"} ${r.estimatedCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : ""}
                  </p>
                </div>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                  SUBMITTED
                </span>
              </Link>
            ))}
          </div>
          <DataPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            size={data.size}
            onPageChange={setPage}
            onSizeChange={() => setPage(0)}
          />
        </>
      )}
    </div>
  );
}
